import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COUNTRIES, DEFAULT_COUNTRY, normalizePhone, parsePhone, parsePhoneInput, validateNational, phoneLengthIssue, type Country } from '../lib/phone';

interface PhoneInputProps {
  // Canonical stored value (IL → `05XXXXXXXX`, others → E.164). The component
  // re-derives flag + digits from this on mount / external change.
  value: string;
  onChange: (normalized: string, valid: boolean) => void;
  // Visual style coming from the campaign form (fsInput) — applied to the
  // combined box so the phone field matches the other inputs 1:1.
  style?: React.CSSProperties;
  placeholder?: string;
  dir?: 'rtl' | 'ltr';
  required?: boolean;
  name?: string;
  id?: string;
  autoComplete?: string;
  ariaRequired?: boolean;
}

// International phone field: flag + dial-code dropdown next to a national-number
// input, with per-country length/format validation. Emits a normalized value so
// downstream (receipts, Bit SMS) always get a clean number — the #1 source of
// failed Bit charges was donors typing "+972-50…" or "972…" or a landline.
export default function PhoneInput({ value, onChange, style, placeholder, dir = 'rtl', required, name, id, autoComplete, ariaRequired }: PhoneInputProps) {
  const initial = useMemo(() => parsePhone(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState<Country>(initial.country || DEFAULT_COUNTRY);
  const [national, setNational] = useState<string>(initial.national || '');
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  // Remember the last value we emitted so the sync-effect below can tell our
  // own echo (parent re-passing it as `value`) apart from a genuine external
  // reset — otherwise it fights the user mid-typing (a lone leading "0" would
  // normalize to "" and get wiped before the next digit).
  const lastEmitted = useRef<string>(normalizePhone(initial.country || DEFAULT_COUNTRY, initial.national || ''));
  const en = dir === 'ltr';

  // Keep internal state in sync only when the parent resets the value
  // externally (a "clear form" or a prefilled donor record arriving late) —
  // never on our own echo.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parsePhone(value);
    setCountry(parsed.country || DEFAULT_COUNTRY);
    setNational(parsed.national || '');
    lastEmitted.current = normalizePhone(parsed.country || DEFAULT_COUNTRY, parsed.national || '');
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!rootRef.current?.contains(target) && !dropdownRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Campaign blocks create their own stacking contexts (transforms/z-index),
  // so an ordinary absolute dropdown can be covered by the amount field or
  // clipped by a block. Position a body-level portal against the phone field.
  useLayoutEffect(() => {
    if (!open) {
      setDropdownPosition(null);
      return;
    }
    const updatePosition = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const width = Math.min(360, Math.max(260, rect.width), viewportWidth - 24);
      const left = Math.max(12, Math.min(rect.left, viewportWidth - width - 12));
      const roomBelow = viewportHeight - rect.bottom;
      const top = roomBelow >= 220 ? rect.bottom + 6 : Math.max(12, rect.top - Math.min(340, viewportHeight * 0.52) - 6);
      setDropdownPosition({ top, left, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [open]);

  const emit = (c: Country, nat: string) => {
    const norm = normalizePhone(c, nat);
    lastEmitted.current = norm;
    onChange(norm, validateNational(c, nat));
  };

  const pickCountry = (c: Country) => {
    setCountry(c);
    setOpen(false);
    setSearch('');
    emit(c, national);
  };

  const onDigits = (raw: string) => {
    // allow digits, spaces and dashes while typing; normalize on emit.
    const cleaned = raw.replace(/[^\d\s()+-]/g, '');
    // Chrome/Android autofill may put a complete "+972..." value here even
    // though +972 is already shown by the separate country picker. Split that
    // full value and, for international donors, update the flag automatically.
    const parsed = parsePhoneInput(country, cleaned);
    setCountry(parsed.country);
    setNational(parsed.national);
    emit(parsed.country, parsed.national);
  };

  // Some Android Chrome versions paint an autofilled value directly into the
  // DOM without dispatching the input/change event React normally receives.
  // Sample the field briefly after mount and normalize only explicit full
  // international values; ordinary typing is never touched by this fallback.
  useEffect(() => {
    const syncInternationalAutofill = () => {
      const raw = inputRef.current?.value || '';
      if (/^\s*(?:\+|00)/.test(raw)) onDigits(raw);
    };
    const timers = [0, 100, 300, 700, 1500].map(ms => window.setTimeout(syncInternationalAutofill, ms));
    return () => timers.forEach(window.clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = !national ? !required : validateNational(country, national);
  const showError = touched && !!national && !valid;

  // Decompose the incoming input style so the OUTER box owns the border/bg and
  // the inner controls stay borderless — gives one seamless field.
  const s = style || {};
  const boxStyle: React.CSSProperties = {
    ...s,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxSizing: 'border-box',
    // Phone numbers are inherently left-to-right — force the field itself to
    // LTR even inside a Hebrew/RTL form, so the flag + dial code sit on the
    // LEFT and digits type left-to-right (matches how people read a phone
    // number). Only the surrounding label/error follow the form's language.
    direction: 'ltr',
    textAlign: 'left',
    borderColor: showError ? '#dc2626' : (s as any).borderColor,
    ...(showError ? { border: '1px solid #dc2626' } : {}),
  };

  const displayedCountries = useMemo(() => {
    const il = COUNTRIES.find(c => c.code === 'IL');
    const rest = COUNTRIES
      .filter(c => c.code !== 'IL')
      .sort((a, b) => a.nameEn.localeCompare(b.nameEn, 'en'));
    return il ? [il, ...rest] : rest;
  }, []);

  const query = search.trim().toLocaleLowerCase(en ? 'en' : 'he');
  const dialQuery = query.replace(/\D/g, '');
  const filtered = query
    ? displayedCountries.filter(c =>
        c.name.toLocaleLowerCase('he').includes(query) ||
        c.nameEn.toLocaleLowerCase('en').includes(query) ||
        c.code.toLowerCase() === query ||
        (!!dialQuery && c.dial.includes(dialQuery)))
    : displayedCountries;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }} dir={dir}>
      <div style={boxStyle}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            border: 'none', background: 'transparent', cursor: 'pointer',
            padding: 0, font: 'inherit', color: 'inherit', whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          aria-label="country code"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span style={{ fontSize: '1.15em', lineHeight: 1 }}>{country.flag}</span>
          <span style={{ opacity: 0.8, direction: 'ltr' as const }}>+{country.dial}</span>
          <span style={{ opacity: 0.5, fontSize: '0.7em' }}>▾</span>
        </button>
        <input
          ref={inputRef}
          type="tel"
          name={name}
          id={id}
          autoComplete={autoComplete}
          inputMode="tel"
          dir="ltr"
          // The dial code (+972) is shown separately, so the placeholder is the
          // NATIONAL part — strip a leading trunk 0 (default "050-0000000" →
          // "50-0000000") so it reads correctly as E.164 next to the prefix.
          placeholder={(placeholder || '').replace(/^0(?=\d)/, '') || (en ? 'Phone number' : 'מספר טלפון')}
          value={national}
          onChange={e => onDigits(e.target.value)}
          onFocus={e => {
            // A focused autofilled field is another reliable point at which
            // Chrome exposes its actual DOM value.
            const el = e.currentTarget;
            requestAnimationFrame(() => onDigits(el.value));
          }}
          onBlur={e => {
            onDigits(e.currentTarget.value);
            setTouched(true);
          }}
          required={required}
          aria-required={ariaRequired || undefined}
          aria-invalid={showError || undefined}
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none',
            background: 'transparent', padding: 0, font: 'inherit', color: 'inherit',
            textAlign: 'left',
          }}
        />
      </div>

      {showError && (
        <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
          {(() => {
            // Prefer a specific digit-count message ("too short / too long for
            // <country>") so the donor knows exactly what's wrong; fall back to
            // a generic invalid-number message for a wrong prefix/pattern.
            const issue = phoneLengthIssue(country, national);
            const cn = en ? country.nameEn : country.name;
            if (issue === 'short') return en ? `Number is too short for ${cn}` : `המספר קצר מדי עבור ${cn}`;
            if (issue === 'long') return en ? `Number is too long for ${cn}` : `המספר ארוך מדי עבור ${cn}`;
            if (country.code === 'IL') return en ? 'Enter a valid Israeli number (e.g. 050-1234567)' : 'יש להזין מספר טלפון ישראלי תקין (לדוגמה 050-1234567)';
            return en ? `Enter a valid phone number for ${cn}` : `יש להזין מספר טלפון תקין עבור ${cn}`;
          })()}
        </div>
      )}

      {open && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          role="listbox"
          aria-label="Countries"
          style={{
            position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left,
            width: dropdownPosition.width,
            maxHeight: 'min(340px, 52vh)', overflowY: 'auto', overscrollBehavior: 'contain',
            background: '#fff', border: '1px solid #dbe2ea', borderRadius: 12,
            boxShadow: '0 14px 38px rgba(15, 23, 42, 0.18)', zIndex: 2147483647,
          }}
        >
          <div style={{ padding: 10, position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid #edf1f5', zIndex: 1 }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search country…"
              aria-label="Search country"
              dir="ltr"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 16, outline: 'none',
                textAlign: 'left', color: '#0f172a', background: '#fff',
              }}
            />
          </div>
          {filtered.map(c => (
            <button
              key={c.code + c.dial}
              type="button"
              role="option"
              aria-selected={c.code === country.code}
              onClick={() => pickCountry(c)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                minHeight: 48, padding: '9px 14px', border: 'none', borderBottom: '1px solid #f1f5f9',
                background: c.code === country.code ? '#eff6ff' : '#fff', color: '#111827',
                cursor: 'pointer', textAlign: 'left', font: 'inherit', direction: 'ltr',
              }}
            >
              <span style={{ fontSize: '1.2em' }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{c.nameEn}</span>
              <span style={{ opacity: 0.6, direction: 'ltr' as const }}>+{c.dial}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 12, color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>
              No matches
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
